import { ActionIcon, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { primaryColor } from '../constants/theme';

export interface CalendarGridProps {
  displayedMonth: Date;
  onMonthChange: (date: Date) => void;
  selectedDates?: Set<string>;
  onDateClick: (date: Date) => void;
  dotIndicators?: Record<string, ('green' | 'orange')[]>;
  disablePast?: boolean;
}

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const CalendarGrid = ({
  displayedMonth,
  onMonthChange,
  selectedDates,
  onDateClick,
  dotIndicators,
  disablePast,
}: CalendarGridProps) => {
  const monthStart = startOfMonth(displayedMonth);
  const monthEnd = endOfMonth(monthStart);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days: Date[] = [];
  let currentDay = gridStart;
  while (currentDay <= gridEnd) {
    days.push(currentDay);
    currentDay = addDays(currentDay, 1);
  }

  const today = startOfDay(new Date());
  const isCurrentMonth = isSameMonth(startOfMonth(new Date()), monthStart);

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => onMonthChange(subMonths(displayedMonth, 1))}
          disabled={disablePast && isCurrentMonth}
          aria-label="Previous month"
        >
          <IconChevronLeft size={18} />
        </ActionIcon>
        <Text fw={600}>{format(displayedMonth, 'MMMM yyyy')}</Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => onMonthChange(addMonths(displayedMonth, 1))}
          aria-label="Next month"
        >
          <IconChevronRight size={18} />
        </ActionIcon>
      </Group>

      <SimpleGrid cols={7} spacing={6}>
        {dayLabels.map((label) => (
          <Text key={label} ta="center" c="dimmed" size="sm" fw={500}>
            {label}
          </Text>
        ))}

        {days.map((day) => {
          const isoDate = format(day, 'yyyy-MM-dd');
          const inCurrentMonth = isSameMonth(day, displayedMonth);
          const isSelected = selectedDates?.has(isoDate) ?? false;
          const isPastDay = isBefore(startOfDay(day), today);
          const isDisabled = !inCurrentMonth || (disablePast && isPastDay);
          const dots = dotIndicators?.[isoDate] ?? [];

          return (
            <Paper
              key={isoDate}
              withBorder={inCurrentMonth}
              radius="md"
              p={4}
              role="button"
              onClick={() => {
                if (!isDisabled) {
                  onDateClick(day);
                }
              }}
              style={{
                minHeight: 52,
                cursor: isDisabled ? 'default' : 'pointer',
                opacity: inCurrentMonth ? 1 : 0.35,
                borderColor: isSelected ? primaryColor : undefined,
                backgroundColor: isSelected ? 'rgba(0, 202, 154, 0.18)' : undefined,
              }}
            >
              <Stack gap={4} align="center" justify="center">
                <Text
                  size="sm"
                  fw={isSameDay(day, today) ? 700 : 500}
                  c={isDisabled ? 'gray.6' : 'dark'}
                >
                  {format(day, 'd')}
                </Text>
                <Group gap={4} justify="center">
                  {dots.map((dot, index) => (
                    <span
                      key={`${isoDate}-${dot}-${index}`}
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: dot === 'green' ? '#00ca9a' : '#f08c00',
                      }}
                    />
                  ))}
                </Group>
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
};
